import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useLocation } from "wouter";
import { AtmosphericPage } from "@/components/MirrorAtmosphere";

export default function Auth() {
  const [mode, setMode] = useState<"login" | "register">("register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const authMutation = useMutation({
    mutationFn: async (data: { email: string; password: string }) => {
      const endpoint = mode === "register" ? "/api/auth/register" : "/api/auth/login";
      const res = await apiRequest("POST", endpoint, data);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      setLocation(
        mode === "register" || !data.onboardingComplete ? "/onboarding" : "/",
      );
    },
    onError: (err: Error) => {
      try {
        const parsed = JSON.parse(err.message.slice(err.message.indexOf("{")));
        if (parsed.error) { setError(parsed.error); return; }
      } catch {}
      setError(
        err.message.includes("409")
          ? "An account with this email already exists"
          : err.message.includes("401")
            ? "Invalid email or password"
            : "Something went wrong. Try again.",
      );
    },
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!email || !password) return setError("Email and password required");
    if (password.length < 6) {
      return setError("Password must be at least 6 characters");
    }
    if (mode === "register" && password !== confirmPassword) {
      return setError("Passwords don't match");
    }
    authMutation.mutate({ email, password });
  };
  return (
    <AtmosphericPage testId="auth-page" atmosphere={{ immersive: true, fill: 0.88, count: 7600, opacityScale: 0.4 }}>
      <div className="mirror-auth-layout">
        <section className="mirror-auth-composition fade-in">
          <header className="mirror-auth-brand">
            <div className="mirror-auth-title" data-testid="text-app-name">FRACTAL</div>
            <div className="mirror-auth-subtitle">pattern intelligence</div>
          </header>
          <div className="mirror-auth-panel">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="mirror-form-label" htmlFor="auth-email">Email</label>
                <input id="auth-email" type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="input-email" className="mirror-form-input" placeholder="you@somewhere.com" autoComplete="email" />
              </div>
              <div>
                <label className="mirror-form-label" htmlFor="auth-password">Password</label>
                <input id="auth-password" type="password" value={password} onChange={e => setPassword(e.target.value)} data-testid="input-password" className="mirror-form-input" placeholder="6+ characters" autoComplete={mode === "register" ? "new-password" : "current-password"} />
              </div>
              {mode === "register" && (
                <div className="fade-in">
                  <label className="mirror-form-label" htmlFor="auth-confirm-password">Confirm password</label>
                  <input id="auth-confirm-password" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} data-testid="input-confirm-password" className="mirror-form-input" placeholder="again" autoComplete="new-password" />
                </div>
              )}
              {error && (
                <p className="type-body text-center py-1" style={{ color: "var(--status-error)" }} data-testid="text-auth-error">
                  {error}
                </p>
              )}
              <button type="submit" disabled={authMutation.isPending} data-testid="button-auth-submit" className="mirror-primary-action">
                {authMutation.isPending ? "..." : mode === "register" ? "Create account" : "Sign in"}
              </button>
            </form>
          </div>
          <div className="mt-6 space-y-3">
            <button onClick={() => { setMode(mode === "register" ? "login" : "register"); setError(""); }} data-testid="button-toggle-auth-mode" className="type-body" style={{ color: "var(--text-muted)" }}>
              {mode === "register" ? "Already have an account? Sign in" : "Need an account? Create one"}
            </button>
            {mode === "login" && <div><button onClick={() => setLocation("/forgot-password")} data-testid="link-forgot-password" className="type-body" style={{ color: "var(--accent-color)" }}>Forgot password?</button></div>}
          </div>
        </section>
      </div>
    </AtmosphericPage>
  );
}